import React, { useEffect, useState, Children } from 'react';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import { PageHeader } from '../components/PageHeader';
import { ShieldCheckIcon, LeafIcon, DollarSignIcon, AwardIcon, CheckCircle2Icon, ArrowRightIcon, GlobeIcon } from 'lucide-react';
const fadeUpVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
function AnimatedCounter({
  target,
  suffix = ''
}: {
  target: number;
  suffix?: string;
}) {
  const count = useMotionValue(0);
  const rounded = useTransform(count, v => Math.round(v));
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const controls = animate(count, target, {
      duration: 2.5,
      delay: 0.2,
      ease: [0.16, 1, 0.3, 1]
    });
    const unsubscribe = rounded.on('change', v => setDisplay(v));
    return () => {
      controls.stop();
      unsubscribe();
    };
  }, [target]);
  return <span data-id="element-279">
      {display}
      {suffix}
    </span>;
}
export function AboutPage() {
  return <div className="bg-white" data-id="element-280">
      <PageHeader title="About GESL" subtitle="A wholly Nigerian-owned multidisciplinary environmental management and consultancy company established in 1994." data-id="element-281" />

      {/* Company Story Section */}
      <section className="py-24 lg:py-32 bg-white overflow-hidden" data-id="element-282">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-283">
          <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-284">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={fadeUpVariants} className="relative order-2 lg:order-1" data-id="element-285">
              <div className="absolute -inset-4 bg-gesl-warmWhite rounded-sm transform -rotate-3 -z-10" data-id="element-286" />
              <img src="https://cdn.magicpatterns.com/uploads/txsFowHtGcMpAgBEEreMoh/pasted-image.png" alt="GESL team recording environmental data in the field" className="w-full h-auto rounded-sm shadow-xl object-cover aspect-[4/5]" data-id="element-287" />
              <div className="absolute -bottom-8 -right-8 bg-gesl-dark text-white p-8 rounded-sm shadow-2xl hidden sm:block border border-white/10" data-id="element-288">
                <div className="text-5xl font-bold text-gesl-gold mb-2" data-id="element-289">
                  2000
                </div>
                <div className="text-sm font-semibold tracking-widest uppercase text-white/80" data-id="element-290">
                  Incorporated
                </div>
              </div>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={staggerContainer} className="order-1 lg:order-2 lg:pl-8" data-id="element-291">
              <motion.div variants={fadeUpVariants} className="flex items-center gap-3 mb-6" data-id="element-292">
                <div className="h-px w-8 bg-gesl-gold" data-id="element-293" />
                <span className="text-gesl-gold font-bold tracking-wider uppercase text-xs" data-id="element-294">
                  Our Story
                </span>
              </motion.div>

              <motion.h2 variants={fadeUpVariants} className="text-3xl md:text-4xl lg:text-5xl font-bold text-gesl-dark mb-8 leading-tight tracking-tight" data-id="element-295">
                Pioneering Sustainable Development in Nigeria
              </motion.h2>

              <motion.div variants={fadeUpVariants} className="space-y-6 text-lg text-gray-600 leading-relaxed mb-10" data-id="element-296">
                <p data-id="element-297">
                  Greenwater Environmental Services Limited (GESL) was created
                  to promote sustainable management of environmental resources,
                  reduce environmental degradation, support responsible
                  development, and provide scientific solutions to complex
                  environmental challenges.
                </p>
                <p data-id="element-298">
                  Over the years, GESL has built a reputation as a dependable
                  consulting partner delivering services in environmental
                  planning, regulatory compliance, environmental monitoring,
                  sustainability advisory, and project support across multiple
                  sectors including oil & gas, infrastructure, power, aviation,
                  telecoms, and manufacturing.
                </p>
              </motion.div>

              <motion.div variants={fadeUpVariants} className="bg-gesl-offWhite p-6 rounded-sm border-l-4 border-gesl-gold" data-id="element-299">
                <h4 className="font-bold text-gesl-dark mb-2" data-id="element-300">
                  Accreditations
                </h4>
                <p className="text-sm text-gray-600 leading-relaxed" data-id="element-301">
                  Registered with the Corporate Affairs Commission (RC 377939).
                  Fully accredited by the Federal Ministry of Environment
                  (FMEnv) and the National Environmental Standards and
                  Regulations Enforcement Agency (NESREA).
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Vision & Mission Section */}
      <section className="relative py-32 lg:py-40 overflow-hidden" data-id="element-302">
        <div className="absolute inset-0" data-id="element-303">
          <img src="https://cdn.magicpatterns.com/uploads/1TmvgZS86z5pY64ht9Jhwq/image52.jpg" alt="GESL team recording environmental data in the field" className="w-full h-full object-cover" data-id="element-304" />
          <div className="absolute inset-0 bg-gesl-dark/80 mix-blend-multiply" data-id="element-305" />
          <div className="absolute inset-0 bg-gradient-to-t from-gesl-dark via-transparent to-transparent" data-id="element-306" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-307">
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12" data-id="element-308">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={fadeUpVariants} className="bg-white/10 backdrop-blur-md border border-white/20 p-10 lg:p-14 rounded-sm" data-id="element-309">
              <div className="flex items-center gap-4 mb-8" data-id="element-310">
                <div className="h-12 w-12 rounded-full bg-gesl-gold/20 flex items-center justify-center" data-id="element-311">
                  <GlobeIcon className="h-6 w-6 text-gesl-gold" data-id="element-312" />
                </div>
                <h3 className="text-2xl font-bold text-white tracking-tight" data-id="element-313">
                  Our Vision
                </h3>
              </div>
              <p className="text-xl lg:text-2xl text-white/90 leading-relaxed font-light" data-id="element-314">
                To lead in delivering solutions that foster a more connected,
                environmentally sustainable world.
              </p>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={fadeUpVariants} className="bg-gesl-green/40 backdrop-blur-md border border-white/20 p-10 lg:p-14 rounded-sm" data-id="element-315">
              <div className="flex items-center gap-4 mb-8" data-id="element-316">
                <div className="h-12 w-12 rounded-full bg-gesl-gold/20 flex items-center justify-center" data-id="element-317">
                  <AwardIcon className="h-6 w-6 text-gesl-gold" data-id="element-318" />
                </div>
                <h3 className="text-2xl font-bold text-white tracking-tight" data-id="element-319">
                  Our Mission
                </h3>
              </div>
              <p className="text-xl lg:text-2xl text-white/90 leading-relaxed font-light" data-id="element-320">
                To be the premier consulting firm recognized for providing
                end-to-end innovative environmental solutions that deliver
                exceptional value and drive sustainability for our clients.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-24 lg:py-32 bg-gesl-warmWhite" data-id="element-321">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-322">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUpVariants} className="text-center max-w-3xl mx-auto mb-16" data-id="element-323">
            <div className="flex items-center justify-center gap-3 mb-4" data-id="element-324">
              <div className="h-px w-8 bg-gesl-gold" data-id="element-325" />
              <span className="text-gesl-gold font-bold tracking-wider uppercase text-xs" data-id="element-326">
                What Drives Us
              </span>
              <div className="h-px w-8 bg-gesl-gold" data-id="element-327" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gesl-dark tracking-tight" data-id="element-328">
              Our Core Values
            </h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid sm:grid-cols-2 gap-6 lg:gap-8" data-id="element-329">
            {[{
            icon: ShieldCheckIcon,
            title: 'Client-Centered Excellence',
            description: 'We prioritize client satisfaction through tailored, practical, and high-quality solutions.'
          }, {
            icon: LeafIcon,
            title: 'Environmental Stewardship',
            description: 'We are committed to responsible environmental decision-making and sustainable resource use.'
          }, {
            icon: DollarSignIcon,
            title: 'Safety & Cost Efficiency',
            description: 'We promote safe operations while ensuring cost-effective project execution.'
          }, {
            icon: AwardIcon,
            title: 'Integrity & Professionalism',
            description: 'We uphold the highest ethical standards in all engagements.'
          }].map((value, idx) => <motion.div key={idx} variants={fadeUpVariants} className="bg-white p-8 lg:p-10 rounded-sm border border-gray-100 shadow-sm" data-id="element-330">
                <div className="h-12 w-12 bg-gesl-offWhite rounded-full flex items-center justify-center mb-6" data-id="element-331">
                  <value.icon className="h-6 w-6 text-gesl-gold" data-id="element-332" />
                </div>
                <h3 className="text-xl font-bold text-gesl-dark mb-3" data-id="element-333">
                  {value.title}
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-334">
                  {value.description}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* Certifications & Memberships */}
      <section className="py-24 lg:py-32 bg-white overflow-hidden" data-id="element-335">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-336">
          <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-337">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={staggerContainer} data-id="element-338">
              <motion.div variants={fadeUpVariants} className="flex items-center gap-3 mb-6" data-id="element-339">
                <div className="h-px w-8 bg-gesl-gold" data-id="element-340" />
                <span className="text-gesl-gold font-bold tracking-wider uppercase text-xs" data-id="element-341">
                  Credentials
                </span>
              </motion.div>

              <motion.h2 variants={fadeUpVariants} className="text-3xl md:text-4xl font-bold text-gesl-dark mb-12 tracking-tight" data-id="element-342">
                Professional Certifications & Memberships
              </motion.h2>

              <div className="space-y-12" data-id="element-343">
                <motion.div variants={fadeUpVariants} data-id="element-344">
                  <h3 className="text-xl font-bold text-gesl-charcoal mb-6 flex items-center gap-3" data-id="element-345">
                    <ShieldCheckIcon className="h-6 w-6 text-gesl-green" data-id="element-346" />
                    Certifications
                  </h3>
                  <ul className="space-y-4" data-id="element-347">
                    {['General Health, Safety & Environment Certification', 'HSE Competence Level 3 (SPDC Accredited)', 'United Nations Environmental Law Training'].map((cert, idx) => <li key={idx} className="flex items-start" data-id="element-348">
                        <CheckCircle2Icon className="h-5 w-5 text-gesl-gold mr-3 mt-0.5 flex-shrink-0" data-id="element-349" />
                        <span className="text-gray-700" data-id="element-350">{cert}</span>
                      </li>)}
                  </ul>
                </motion.div>

                <motion.div variants={fadeUpVariants} data-id="element-351">
                  <h3 className="text-xl font-bold text-gesl-charcoal mb-6 flex items-center gap-3" data-id="element-352">
                    <AwardIcon className="h-6 w-6 text-gesl-green" data-id="element-353" />
                    Memberships
                  </h3>
                  <ul className="space-y-4" data-id="element-354">
                    {['Nigerian Environmental Society', 'Nigerian Conservation Foundation', 'International Environmental Forum', 'International Safety Quality Environment Association (UK)', 'International Association of Engineers & Computer Scientists'].map((member, idx) => <li key={idx} className="flex items-start" data-id="element-355">
                        <CheckCircle2Icon className="h-5 w-5 text-gesl-gold mr-3 mt-0.5 flex-shrink-0" data-id="element-356" />
                        <span className="text-gray-700" data-id="element-357">{member}</span>
                      </li>)}
                  </ul>
                </motion.div>
              </div>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={fadeUpVariants} className="relative" data-id="element-358">
              <div className="absolute -inset-4 bg-gesl-offWhite rounded-sm transform rotate-3 -z-10" data-id="element-359" />
              <img src="https://cdn.magicpatterns.com/uploads/bMSggf41iRuC8HrSGrT3Z4/image63.jpg" alt="GESL team at field site visit" className="w-full h-auto rounded-sm shadow-xl object-cover aspect-[4/5]" data-id="element-360" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gesl-dark border-t border-white/10" data-id="element-361">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-362">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={staggerContainer} className="grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-12 divide-x-0 md:divide-x divide-white/10" data-id="element-363">
            {[{
            value: 30,
            suffix: '+',
            label: 'Years Experience'
          }, {
            value: 200,
            suffix: '+',
            label: 'Projects Delivered'
          }, {
            value: 9,
            suffix: '+',
            label: 'Industries Served'
          }, {
            value: 100,
            suffix: '%',
            label: 'Nigerian Owned'
          }].map((stat, idx) => <motion.div key={idx} variants={fadeUpVariants} className={`text-center ${idx % 2 !== 0 ? 'border-l border-white/10 md:border-none pl-8 md:pl-0' : ''} ${idx > 1 ? 'pt-8 border-t border-white/10 md:border-none md:pt-0' : ''}`} data-id="element-364">
                <div className="text-4xl md:text-5xl lg:text-6xl font-bold text-gesl-gold mb-3 tracking-tight" data-id="element-365">
                  <AnimatedCounter target={stat.value} suffix={stat.suffix} data-id="element-366" />
                </div>
                <div className="text-white/60 text-xs font-bold uppercase tracking-widest" data-id="element-367">
                  {stat.label}
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* Client Profile Section */}
      <section className="py-24 lg:py-32 bg-gesl-offWhite" data-id="element-368">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-369">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUpVariants} className="text-center max-w-3xl mx-auto mb-16" data-id="element-370">
            <div className="flex items-center justify-center gap-3 mb-4" data-id="element-371">
              <div className="h-px w-8 bg-gesl-gold" data-id="element-372" />
              <span className="text-gesl-gold font-bold tracking-wider uppercase text-xs" data-id="element-373">
                Who We Serve
              </span>
              <div className="h-px w-8 bg-gesl-gold" data-id="element-374" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gesl-dark tracking-tight" data-id="element-375">
              Our Client Profile
            </h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-376">
            {['Federal & State Governments', 'International Development Agencies', 'Oil & Gas Operators', 'Power & Energy Developers', 'Telecom Operators', 'Manufacturing Companies', 'Infrastructure Developers', 'Agricultural Enterprises'].map((client, idx) => <motion.div key={idx} variants={fadeUpVariants} className="bg-white p-6 rounded-sm border border-gray-100 shadow-sm flex items-center gap-3" data-id="element-377">
                <CheckCircle2Icon className="h-5 w-5 text-gesl-gold flex-shrink-0" data-id="element-378" />
                <span className="text-gesl-charcoal font-medium text-sm" data-id="element-379">
                  {client}
                </span>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-gesl-green to-gesl-dark relative overflow-hidden" data-id="element-380">
        <div className="absolute top-0 right-0 w-64 h-64 bg-gesl-gold/10 rounded-full blur-3xl" data-id="element-381" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full blur-3xl" data-id="element-382" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-383">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUpVariants} data-id="element-384">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 tracking-tight" data-id="element-385">
              Ready to Partner With Us?
            </h2>
            <p className="text-lg md:text-xl text-white/80 mb-10 max-w-2xl mx-auto font-light" data-id="element-386">
              Get in touch for a consultation on your next environmental
              project. Our team of experts is ready to deliver sustainable
              solutions.
            </p>
            <a href="#contact" className="inline-flex items-center gap-2 px-8 py-4 bg-gesl-gold text-white font-bold rounded-sm hover:bg-white hover:text-gesl-dark transition-colors duration-300 shadow-lg group" data-id="element-387">
              Contact Us Today
              <ArrowRightIcon className="h-5 w-5 group-hover:translate-x-1 transition-transform" data-id="element-388" />
            </a>
          </motion.div>
        </div>
      </section>
    </div>;
}